import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, Section, Loading, DataTable, StatusPill } from "../components/Common";
import { Award, Send } from "lucide-react";
import { toast } from "sonner";

async function openArchive(id) {
  const token = localStorage.getItem("edu360.token");
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/education360/publishing/archive/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) return toast.error("Unable to open PDF");
  window.open(URL.createObjectURL(await res.blob()), "_blank");
}

export default function Credentials() {
  const [types, setTypes] = useState({});
  const [students, setStudents] = useState([]);
  const [issued, setIssued] = useState([]);
  const [selected, setSelected] = useState("");
  const [type, setType] = useState("transcript");
  const [busy, setBusy] = useState(false);

  const loadIssued = () => api.archive().then(({ data }) =>
    setIssued((data.documents || []).filter((d) =>
      d.document_type === "transcript" || (d.document_type || "").startsWith("credential_")
    ))
  );

  useEffect(() => {
    api.credentialTypes().then(({ data }) => setTypes({ transcript: "Official Academic Transcript", ...data }));
    api.students().then(({ data }) => setStudents(data));
    loadIssued();
  }, []);

  const issue = async () => {
    if (!selected) return toast.error("Select a student");
    setBusy(true);
    try {
      const { data } = await api.issueCredential(selected, type);
      toast.success(`${types[type]} issued · v${data.version}`);
      loadIssued();
    } catch { toast.error("Issue failed"); }
    setBusy(false);
  };

  return (
    <div data-testid="credentials-page">
      <PageHeader
        kicker="Academic Credentials Centre"
        title="Transcripts, Certificates & Awards"
        subtitle="Issue premium HMAC-signed credentials — transcripts, diplomas, certificates and awards. Every credential is archived with QR verification."
      />
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="hl-card p-6 space-y-4">
          <div className="overline">Issue a credential</div>
          <div>
            <label className="overline block mb-1.5">Student</label>
            <select value={selected} onChange={(e) => setSelected(e.target.value)}
              data-testid="cred-student" className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
              <option value="">— select —</option>
              {students.map((s) => <option key={s.id} value={s.id}>{s.first_name} {s.last_name} · {s.year_group}</option>)}
            </select>
          </div>
          <div>
            <label className="overline block mb-1.5">Credential Type</label>
            <select value={type} onChange={(e) => setType(e.target.value)}
              data-testid="cred-type" className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
              {Object.entries(types).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <button onClick={issue} disabled={busy || !selected}
            data-testid="cred-issue"
            className="hl-btn-accent inline-flex items-center gap-2 text-sm disabled:opacity-40">
            <Award className="w-4 h-4" /> {busy ? "Issuing…" : "Issue & Archive"}
          </button>
          <div className="text-xs text-slate-500">Every issued credential is HMAC-signed, QR-verifiable and permanently archived.</div>
        </div>
        <div className="hl-card p-6">
          <div className="overline mb-2">Supported credentials</div>
          <ul className="text-sm text-slate-700 grid grid-cols-2 gap-x-4 gap-y-1.5">
            {Object.entries(types).map(([k, v]) => (
              <li key={k} className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[color:var(--hl-orange)]" />{v}</li>
            ))}
          </ul>
        </div>
      </div>

      <Section title="Issued credentials" testId="credentials-list">
        <DataTable
          columns={[
            { key: "subject_label", label: "Credential" },
            { key: "document_type", label: "Type", render: (r) => <StatusPill status="info" /> },
            { key: "verification_id", label: "Reference", render: (r) => <span className="mono text-xs">{r.verification_id}</span> },
            { key: "signature", label: "Signature", render: (r) => <span className="mono text-xs text-emerald-700">{(r.signature || "").slice(0, 10)}…</span> },
            { key: "version", label: "Version", render: (r) => `v${r.version}` },
            { key: "size_bytes", label: "Size", render: (r) => `${((r.size_bytes || 0) / 1024).toFixed(0)} KB` },
            { key: "created_at", label: "Issued", render: (r) => (r.created_at || "").slice(0, 16) },
            { key: "actions", label: "PDF", render: (r) => (
              <button onClick={() => openArchive(r.id)}
                data-testid={`cred-open-${r.id}`}
                className="text-[color:var(--hl-orange)] hover:underline text-xs font-semibold">Open</button>
            ) },
          ]}
          rows={issued}
          empty="No credentials issued yet."
        />
      </Section>
    </div>
  );
}
